<script setup>
import { computed, onMounted, ref } from 'vue';
import { useSiteForm } from '../useSiteForm.js';
import { styleVars } from './shared.js';
import EmailTypoHint from '@/Components/EmailTypoHint.vue';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const { values, errors, status, message, submit, turnstileKey, mountTurnstile } = useSiteForm('contact', {
    siteData: props.site_data,
    fields: { name: '', email: '', message: '' },
});

const turnstileEl = ref(null);
onMounted(() => { if (turnstileKey) mountTurnstile(turnstileEl.value); });

const showForm = computed(() => props.config.show_form !== false);

// An email address becomes mailto:, something that is only a phone
// number becomes tel:, anything else (a postal address) stays text.
function detailHref(value) {
    const v = String(value ?? '').trim();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return `mailto:${v}`;
    const digits = v.replace(/\D/g, '');
    if (/^\+?[\d\s().-]+$/.test(v) && digits.length >= 7 && digits.length <= 15) {
        return `tel:${v.trim().startsWith('+') ? '+' : ''}${digits}`;
    }
    return null;
}

const details = computed(() => (Array.isArray(props.config.details) ? props.config.details : [])
    .filter((d) => d && String(d.value ?? '').trim() !== '')
    .map((d) => ({ label: d.label || '', value: String(d.value).trim(), href: detailHref(d.value) })));

const successText = computed(() => props.config.success_message || message.value);
</script>

<template>
    <section :id="config.anchor_id || null" class="th-block th-contact th-band" :style="styleVars(config)">
        <div class="th-wrap th-contact__grid" :class="{ 'th-contact__grid--solo': !showForm }">
            <div class="th-stack th-contact__info">
                <h2 v-if="config.heading" class="th-h2">{{ config.heading }}</h2>
                <div v-for="(d, i) in details" :key="i" class="th-contact__detail">
                    <div v-if="d.label" class="th-mono th-mono--caps th-contact__label">{{ d.label }}</div>
                    <a v-if="d.href" :href="d.href" class="th-contact__value th-contact__link">{{ d.value }}</a>
                    <div v-else class="th-contact__value">{{ d.value }}</div>
                </div>
            </div>

            <form v-if="showForm" class="th-card th-stack th-contact__form" novalidate @submit.prevent="submit()">
                <div v-if="status === 'sent'" class="th-form-note th-form-note--ok th-contact__sent" role="status">{{ successText }}</div>
                <template v-else>
                    <label class="th-label">Name
                        <input v-model="values.name" class="th-input th-contact-name" type="text" name="name" autocomplete="name">
                        <span v-if="errors.name" class="th-field-error">{{ errors.name }}</span>
                    </label>
                    <label class="th-label">Email
                        <input v-model="values.email" class="th-input th-contact-email" type="email" name="email" autocomplete="email">
                        <EmailTypoHint :email="values.email" @fix="values.email = $event" />
                        <span v-if="errors.email" class="th-field-error">{{ errors.email }}</span>
                    </label>
                    <label class="th-label">Message
                        <textarea v-model="values.message" class="th-input th-contact-message" name="message" rows="4"></textarea>
                        <span v-if="errors.message" class="th-field-error">{{ errors.message }}</span>
                    </label>
                    <input v-model="values.website" class="th-hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
                    <div v-if="turnstileKey" ref="turnstileEl" class="th-contact__turnstile"></div>
                    <div v-if="status === 'error' && message" class="th-form-note th-form-note--error" role="alert">{{ message }}</div>
                    <button type="submit" class="th-btn th-btn--primary th-contact__submit th-contact-submit" :disabled="status === 'sending'">
                        {{ status === 'sending' ? 'Sending…' : (config.button_label || 'Send message') }}
                    </button>
                </template>
            </form>
        </div>
    </section>
</template>

<style scoped>
.th-contact__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
    gap: clamp(24px, 4vw, 56px);
    align-items: start;
}
.th-contact__grid--solo { grid-template-columns: minmax(0, 1fr); }
.th-contact__info { --th-gap: 20px; }
.th-contact__detail { display: flex; flex-direction: column; gap: 4px; }
.th-contact__label { font-size: 11px; letter-spacing: .1em; margin-bottom: 4px; }
.th-contact__value { font-size: 17px; overflow-wrap: anywhere; }
.th-contact__link { text-decoration: underline; text-decoration-color: var(--accent); text-underline-offset: 3px; }
.th-contact__form { padding: clamp(20px, 3vw, 32px); }
.th-contact__form .th-input { padding-top: 12px; padding-bottom: 12px; }
.th-contact__submit { align-self: flex-start; }
</style>
